import { CardContent, CardHeader } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';

import { useGetDeviceConnections } from '@hooks/api/scan';
import { useGetUser } from '@hooks/api/user';

import { formatDate } from '@utils/format';
import { formatName } from '@utils/format';

const messages = defineMessages({
  connectDevices: {
    id: 'clientProfileCard.connectDevices',
    defaultMessage: 'Connected Health Devices and Apps',
  },
  subtitle: {
    id: 'clientProfileCard.subtitle',
    defaultMessage: '{name} {text} devices and/or apps connected',
  },
  devicesAndApps: {
    id: 'clientProfileCard.devicesAndApps',
    defaultMessage: 'Health Devices and Apps',
  },
  connectionDate: {
    id: 'clientProfileCard.connectionDate',
    defaultMessage: 'Date connection established',
  },
  dateConnected: {
    id: 'devicesMain.dateConnected',
    defaultMessage: 'Date Connected: {date}',
  },
  lastUpdate: {
    id: 'devicesMain.lastUpdate',
    defaultMessage: 'Last Update: {date}',
  },
});

const DevicesCard: React.FC<{ user_id: string }> = ({ user_id }) => {
  const { formatMessage } = useIntl();

  const { data: deviceConnections, loading: deviceConnectionsLoading } =
    useGetDeviceConnections();
  const { data: userData, loading: userLoading } = useGetUser({
    id: user_id,
  });
  const user = userData?.getUserRecord;
  const { first_name, last_name } = user ?? {};

  const loading = userLoading || deviceConnectionsLoading;

  return (
    <ContentLoader loading={loading}>
      <CardHeader
        subheader={formatMessage(messages.subtitle, {
          name: formatName(first_name, last_name),
          text: deviceConnections.length
            ? 'has the following'
            : 'does not have any',
        })}
        title={formatMessage(messages.connectDevices)}
        slotProps={{
          title: {
            sx: {
              color: (theme) => theme.palette.primary.main,
            },
          },
        }}
      />
      <CardContent>
        {deviceConnections.length > 0 && (
          <MuiDataTable
            columns={[
              {
                name: 'integration',
                label: 'Integration',
              },
              {
                name: 'created_at',
                className: 'small',
                label: 'Date Connected',
                options: {
                  customBodyRender: (value: string) =>
                    formatDate({
                      date: value,
                    }),
                },
              },
              {
                name: 'last_webhook_update',
                className: 'small',
                label: 'Last Update',
                options: {
                  customBodyRender: (value?: string) =>
                    formatDate({ date: value, fallback: '-' }),
                },
              },
              {
                name: 'terra_connection_id',
                className: 'small',
                label: 'Device ID (Terra User ID)',
              },
            ]}
            data={deviceConnections}
          />
        )}
      </CardContent>
    </ContentLoader>
  );
};

export default DevicesCard;
